import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChefHat, Eye, EyeOff, Loader2, Check, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const RegisterPage = () => {
    const { register } = useAuth();
    const navigate = useNavigate();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!name || !email || !password || !confirmPassword) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        
        if (password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        
        setLoading(true);
        try {
            await register(email, password, name);
            toast.success('Compte créé avec succès!');
            navigate('/');
        } catch (error) {
            console.error('Register error:', error);
            toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
        } finally {
            setLoading(false);
        }
    };

    const benefits = [
        { text: 'Sauvegardez vos recettes préférées', color: 'text-green-500' },
        { text: 'Créez et partagez vos recettes', color: 'text-orange-500' },
        { text: 'Accédez aux astuces économiques', color: 'text-purple-500' },
        { text: 'Gratuit et sans engagement', color: 'text-pink-500' }
    ];

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-green top-10 -right-20 opacity-30" />
            <div className="blob-orange bottom-10 -left-20 opacity-30" />
            <div className="blob-purple top-1/3 left-10 opacity-20" />
            
            <div className="w-full max-w-md relative z-10">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
                    <div className="p-3 bg-gradient-cameleon rounded-2xl border-2 border-foreground shadow-pop group-hover:shadow-pop-hover transition-all">
                        <ChefHat className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <span className="font-heading font-black text-2xl block">Recettes</span>
                        <span className="font-heading font-black text-2xl text-gradient-cameleon -mt-1 block">Caméléon</span>
                    </div>
                </Link>
                
                {/* Card */}
                <div className="bg-white rounded-3xl border-2 border-foreground shadow-[8px_8px_0px_0px_#1e293b] p-8">
                    <div className="text-center mb-6">
                        <h1 className="font-heading text-3xl font-black text-foreground mb-2">Créer un compte</h1>
                        <p className="text-muted-foreground">
                            Rejoignez notre communauté de cuisiniers malins
                        </p>
                    </div>
                    
                    {/* Benefits */}
                    <div className="bg-gradient-to-br from-green-50 to-purple-50 rounded-2xl p-5 mb-6 border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-purple-500" />
                            <span className="font-heading font-bold text-foreground">Avantages inclus</span>
                        </div>
                        <ul className="space-y-2">
                            {benefits.map((benefit, index) => (
                                <li key={index} className="flex items-center gap-3">
                                    <div className={`w-6 h-6 rounded-full ${benefit.color.replace('text-', 'bg-')} flex items-center justify-center flex-shrink-0`}>
                                        <Check className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-sm text-foreground font-medium">{benefit.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="font-bold">Nom</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Votre nom"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="rounded-xl border-2 border-foreground py-5 focus:shadow-pop transition-shadow"
                                data-testid="register-name-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-bold">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="rounded-xl border-2 border-foreground py-5 focus:shadow-pop transition-shadow"
                                data-testid="register-email-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="password" className="font-bold">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="rounded-xl border-2 border-foreground py-5 pr-12 focus:shadow-pop transition-shadow"
                                    data-testid="register-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="font-bold">Confirmer le mot de passe</Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="rounded-xl border-2 border-foreground py-5 focus:shadow-pop transition-shadow"
                                data-testid="register-confirm-password-input"
                            />
                        </div>
                        
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-pop bg-primary text-white py-6 text-lg mt-2"
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Création...
                                </>
                            ) : (
                                <>
                                    Créer mon compte
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                    </form>
                    
                    <div className="mt-6 text-center">
                        <p className="text-muted-foreground">
                            Déjà inscrit?{' '}
                            <Link to="/login" className="text-primary font-bold hover:underline" data-testid="login-link">
                                Se connecter
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
